import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardMedia, Typography, Box, Button, Link, IconButton } from '@mui/material';
import { normalizeImageUrl } from './Post';
import './Advertisement.css';
import advertisementService from '../utils/advertisementService';
import { useAuth } from '../contexts/AuthContext';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const Advertisement = ({
  id,
  title,
  description,
  image,
  url,
  companyName,
  type, // 'top' or 'feed'
}) => {
  // 広告要素への参照
  const adRef = useRef(null);
  // インプレッション記録済みフラグ
  const impressionRecorded = useRef(false);
  // いいね、ブックマーク状態
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  // ユーザー情報を取得
  const { currentUser, userRole, isLoggedIn } = useAuth();
  // ナビゲーションフック追加
  const navigate = useNavigate();

  // 広告表示検出とインプレッション記録
  useEffect(() => {
    // インプレッション記録関数
    const recordImpression = () => {
      if (!impressionRecorded.current && id && id !== 'recruitment') {
        // まだ記録されていない場合のみ記録（recruitment除く）
        advertisementService.recordImpression(id);
        impressionRecorded.current = true;
      }
    };

    // Intersection Observerの設定
    const observer = new IntersectionObserver(
      (entries) => {
        // 要素が表示領域に入った場合
        if (entries[0].isIntersecting) {
          recordImpression();
          // 一度記録したら監視を解除
          observer.disconnect();
        }
      },
      {
        // 少なくとも50%表示されたらインプレッションとカウント
        threshold: 0.5,
        // ルート要素（ビューポート）
        root: null
      }
    );

    // 要素が存在する場合のみ監視を開始
    if (adRef.current) {
      observer.observe(adRef.current);
    }

    // コンポーネントのクリーンアップ時に監視を解除
    return () => {
      observer.disconnect();
    };
  }, [id]);

  // 広告のステータスを取得
  useEffect(() => {
    // ログイン済みかつ通常の広告の場合のみ実行
    if (isLoggedIn && id && id !== 'recruitment') {
      // 広告のいいね状態を取得
      advertisementService.getAdStatus(id)
        .then(status => {
          setLiked(status.liked || false);
          setBookmarked(status.bookmarked || false);
          setLikeCount(status.likeCount || 0);
        })
        .catch(error => {
          
        });
    }
  }, [isLoggedIn, id]);

  // 広告クリック時の処理
  const handleAdClick = async () => {
    // 掲載店募集中の場合は直接リンクに遷移
    if (id === 'recruitment') {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    // ユーザーロールの取得（デフォルトは'viewer'）
    const role = userRole || 'viewer';
    
    // 広告クリックを記録
    try {
      const result = await advertisementService.recordAdClick(id, role);
      
      
      // 予算切れの場合のログ
      if (result.success && result.data?.budget_depleted) {
        
      }
    } catch (error) {
      
    }
    
    // 外部リンクに遷移
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // いいねボタン処理
  const handleLike = async (e) => {
    e.stopPropagation(); // イベントの伝播を停止

    if (id === 'recruitment') {
      return; // 掲載店募集中の場合は何もしない
    }

    if (!isLoggedIn) {
      toast.info('いいねするにはログインが必要です');
      return;
    }

    try {
      // 楽観的UI更新
      setLiked(!liked);
      setLikeCount(prev => liked ? prev - 1 : prev + 1);

      // APIコール
      const result = await advertisementService.likeAd(id, !liked);
      
      if (!result.success) {
        // 失敗した場合は元に戻す
        setLiked(liked);
        setLikeCount(prev => liked ? prev + 1 : prev - 1);
        toast.error('いいねの処理中にエラーが発生しました');
      } else {
        // 成功時にサーバーからの正確ないいね数で更新
        if (result.data && typeof result.data.likeCount === 'number') {
          setLikeCount(result.data.likeCount);
        }
      }
    } catch (error) {
      // エラーが発生した場合は元に戻す
      setLiked(liked);
      setLikeCount(prev => liked ? prev + 1 : prev - 1);
      
      toast.error(error.message || 'いいねの処理中にエラーが発生しました');
    }
  };

  // ブックマークボタン処理
  const handleBookmark = async (e) => {
    e.stopPropagation(); // イベントの伝播を停止

    if (id === 'recruitment') {
      return; // 掲載店募集中の場合は何もしない
    }

    if (!isLoggedIn) {
      toast.info('ブックマークするにはログインが必要です');
      return;
    }

    try {
      // 楽観的UI更新
      setBookmarked(!bookmarked);

      // APIコール
      const result = await advertisementService.bookmarkAd(id, !bookmarked);
      
      if (!result.success) {
        // 失敗した場合は元に戻す
        setBookmarked(bookmarked);
        toast.error('ブックマークの処理中にエラーが発生しました');
      }
    } catch (error) {
      // エラーが発生した場合は元に戻す
      setBookmarked(bookmarked);
      
      toast.error(error.message || 'ブックマークの処理中にエラーが発生しました');
    }
  };

  // シェアボタン処理
  const handleShare = async (e) => {
    e.stopPropagation(); // イベントの伝播を停止

    try {
      // シェアURLを作成
      const shareUrl = url || window.location.href;
      const shareText = `${title} - ${companyName}からの広告`;

      // ネイティブシェアAPIが利用可能な場合はそれを使用
      if (navigator.share) {
        await navigator.share({
          title: title,
          text: shareText,
          url: shareUrl
        });
      } else {
        // クリップボードにコピー
        await navigator.clipboard.writeText(shareUrl);
        toast.success('リンクをクリップボードにコピーしました');
      }

      // 掲載店募集中以外の場合のみクリックとしてカウント
      if (id !== 'recruitment') {
        await advertisementService.recordAdClick(id, userRole || 'viewer', 'share');
      }
    } catch (error) {
      if (error.name !== 'AbortError') { // ユーザーがシェアをキャンセルした場合を除く
        
        toast.error('シェアの処理中にエラーが発生しました');
      }
    }
  };

  // ユーザープロフィールに移動する関数
  const handleCompanyClick = (e) => {
    e.stopPropagation(); // 親要素のクリックイベントを停止
    // 会社名から抽出されたusernameを使用してプロフィールページに遷移
    const username = companyName.split('@')[0].trim(); // @がある場合は分割して最初の部分を使用
    navigate(`/profile/${username}`);
  };

  return (
    <Card 
      ref={adRef}
      className={`advertisement ${type === 'top' ? 'advertisement--top' : 'advertisement--feed'}`}
    >
      <Box className="advertisement__pr-badge">
        <Typography variant="caption">PR</Typography>
      </Box>
      
      {image && (
        <CardMedia
          component="img"
          height={type === 'top' ? '300' : '300'}
          image={normalizeImageUrl(image)}
          alt={title}
          className="advertisement__image"
          sx={{ 
            objectFit: 'contain', 
            aspectRatio: '2/1',
            width: '100%',
            maxWidth: '100%',
            backgroundColor: '#f5f5f5'
          }}
        />
      )}
      
      <CardContent className="advertisement__content">
        <Typography variant="subtitle1" component="div" className="advertisement__title">
          {title}
        </Typography>
        
        {/* 説明文が存在し、かつタイトルと異なる場合のみ表示 */}
        {description && description.trim() !== '' && description.trim() !== title.trim() && (
          <Typography variant="body2" color="text.secondary" className="advertisement__description">
            {description
              .replace(/<br\s*\/?>/gi, '\n') // HTMLのbrタグを改行文字に変換
              .split('\n')
              .map((line, index, array) => (
                <React.Fragment key={index}>
                  {line.trim()}
                  {index < array.length - 1 && <br />}
                </React.Fragment>
              ))
            }
          </Typography>
        )}
        
        <Box className="advertisement__footer" mt={1}>
          <Typography variant="caption" color="text.secondary">
            提供: <span 
              onClick={handleCompanyClick} 
              style={{ 
                cursor: 'pointer', 
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {companyName}
            </span>
          </Typography>
          
          <Button 
            size="small" 
            variant="contained" 
            color="primary" 
            onClick={handleAdClick}
            className="advertisement__button"
          >
            詳細を見る
          </Button>
        </Box>

        {/* アクションボタン */}
        <Box className="advertisement__actions" mt={1} display="flex" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <IconButton 
              size="small" 
              onClick={handleLike}
              color={liked ? "error" : "default"}
              className="advertisement__action-button"
              disabled={id === 'recruitment'}
            >
              {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
            </IconButton>
            {likeCount > 0 && (
              <Typography variant="caption" color="text.secondary" ml={0.5}>
                {likeCount}
              </Typography>
            )}
          </Box>
          
          <IconButton 
            size="small" 
            onClick={handleBookmark}
            color={bookmarked ? "primary" : "default"}
            className="advertisement__action-button"
            disabled={id === 'recruitment'}
          >
            {bookmarked ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
          </IconButton>
          
          <IconButton 
            size="small" 
            onClick={handleShare}
            className="advertisement__action-button"
          >
            <ShareIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Advertisement; 